/*
 * Copyright (c) Nexign, JSC, 2025.
 */
/**
 * DChat config
 *
 * @author Nikolay Sviridov
 * @version 1.38.0
 * @since 1.38.0
 */

import { fonts, colors as palette, icons as themeIcons } from '@theme/index';

export const dchatContainerId = 'dchat-container';
export const chatId = 'chat-script';
export const defaultChatSrc = 'https://cdn-afina.megafon.ru/hermes/scripts/mf/hermes.js';

const {
	BrandColorMain,
	BrandColorMainHover,
	ColorError,
	ColorGreenDark,
	ColorGreyLight2,
	ColorWhite,
	ColorGrey1,
	ColorGrey3,
	SpbSky0,
	SpbSky1,
	SpbSky2,
	SpbSky3,
	BrandColorMainLighter,
	BrandColorMainLighten,
	BrandColorMainDarken,
	ButtonDown,
	LightSoftGreen,
	Fury,
	ButtonHoverGreen,
	ColorWarning,
	LightFury,
	BrandPurple,
	Purple,
	DarkPeach,
} = palette;

const userData = {
	businessChannel: 'lk_b2b',
};
const metrics = {
	closeBtnClass: '',
};

const medias = {
	authError: themeIcons.statusError,
};

const buttonsMain = {
	elevated: {
		background: {
			enabled: {
				//
				default: BrandColorMain, //
				hover: BrandColorMainHover, // :hover
				focus: BrandColorMainHover, // :focus
				active: ColorGreenDark, // :active
			},
			disabled: {
				//
				default: ColorGreyLight2, //
			},
		},
		color: {
			enabled: {
				//
				default: ColorWhite, //
				hover: ColorWhite, // :hover
				focus: ColorWhite, // :focus
				active: ColorWhite, // :active
			},
			disabled: {
				//
				default: SpbSky3, //
			},
		},
		border: {
			enabled: {
				//
				default: '', //
				hover: '', // :hover
				focus: '', // :focus
				active: '',
			},
			disabled: {
				//
				default: SpbSky2, //
			},
		},
	},
	outlined: {
		background: {
			enabled: {
				default: ColorWhite,
				hover: BrandColorMainLighter,
				focus: BrandColorMainLighter,
				active: BrandColorMainLighten,
			},
			disabled: {
				default: ColorWhite,
			},
		},
		color: {
			enabled: {
				default: BrandColorMain,
				hover: BrandColorMain,
				focus: BrandColorMain,
				active: BrandColorMain,
			},
			disabled: {
				default: SpbSky3,
			},
			border: {
				enabled: {
					default: BrandColorMain,
					hover: BrandColorMain,
					focus: BrandColorMain,
					active: BrandColorMain,
				},
				disabled: {
					default: SpbSky2,
				},
			},
		},
	},
};

const buttonsText = {
	text: {
		color: {
			//
			default: BrandColorMain, //
			disabled: SpbSky3, //
			hover: BrandColorMainHover, // :hover
			focus: BrandColorMainHover, // :focus
			active: ColorGreenDark, // :active
		},
	},
};

const buttonsIcon = {
	elevated: {
		fill: {
			//
			default: ColorWhite, //
			hover: ColorWhite, // :hover
			focus: ColorWhite, // :focus
			press: ColorWhite, // :active
			disabled: SpbSky3, //
		},
		background: {
			//
			default: BrandColorMain, //
			hover: BrandColorMainHover, // :hover
			focus: BrandColorMainHover, // :focus
			press: ColorWhite, // :active
			disabled: SpbSky1, //
		},
		border: {
			//
			default: '', //
			hover: '', // :hover
			focus: '', // :focus
			press: '', // :active
			disabled: SpbSky2, //
		},
	},
	outlined: {
		fill: {
			//
			default: BrandColorMain, //
			hover: BrandColorMain, // :hover
			focus: BrandColorMain, // :focus
			press: BrandColorMain, // :active
			disabled: SpbSky3, //
		},
		background: {
			//
			default: ColorWhite, //
			hover: ColorGreenDark, // :hover
			focus: ColorGreenDark, // :focus
			press: BrandColorMainDarken, // :active
			disabled: ColorWhite, //
		},
		border: {
			//
			default: BrandColorMain, //
			hover: BrandColorMain, // :hover
			focus: BrandColorMain, // :focus
			press: '', // :active
			disabled: BrandColorMain, //
		},
	},
};

const buttonsFab = {
	fab: {
		active: {
			//
			fill: {
				//
				default: ColorWhite, //
				hover: ColorWhite, // :hover
				focus: ColorWhite, // :focus
				press: ColorWhite, // :active
			},
			background: {
				//
				default: BrandColorMain, //
				hover: BrandColorMainHover, // :hover
				focus: BrandColorMainHover, // :focus
				press: ButtonDown, // :active
			},
		},
		passive: {
			//
			fill: {
				//
				default: ColorGrey1, //
				hover: ColorGrey1, // :hover
				focus: ColorGrey1, // :focus
				press: ColorWhite, // :active
			},
			background: {
				//
				default: ColorWhite, //
				hover: SpbSky1, // :hover
				focus: SpbSky1, // :focus
				press: ButtonDown, // :active
			},
		},
	},
};

const buttonsPredictive = {
	normal: {
		//
		background: {
			//
			default: BrandColorMain, //
			hover: BrandColorMainHover, // :hover
			active: ColorGreenDark, // :active
			disabled: SpbSky1, //
		},
		border: {
			//
			default: '', //
			hover: '', // :hover
			active: '', // :active
			disabled: SpbSky1, //
		},
		text: {
			default: ColorWhite, //
			hover: ColorWhite, // :hover
			active: ColorWhite, // :active
			disabled: SpbSky3, //
		},
	},
	big: {
		background: {
			default: LightSoftGreen, //
			hover: ButtonHoverGreen, // :hover
			active: ColorGreenDark, // :active
		},
		fill: {
			//
			default: SpbSky3, //
			hover: SpbSky3, // :hover
			active: SpbSky3, // :active
		},
		text: {
			//
			default: ColorGrey1, //
			hover: ColorGrey1, // :hover
			active: ColorWhite, // :active
		},
	},
};

const buttons = {
	main: buttonsMain,
	text: buttonsText,
	icon: buttonsIcon,
	fab: buttonsFab,
	predictive: buttonsPredictive,
};

const input = {
	background: {
		//
		default: ColorWhite, //
		disabled: SpbSky0, //
		hover: ColorWhite, // :hover
		focus: ColorWhite, // :focus
		error: ColorWhite, //
	},
	placeholder: {
		// (placeholder)
		default: SpbSky3, //
		disabled: SpbSky3, //
		hover: SpbSky3, // :hover
		focus: SpbSky3, // :focus
		error: SpbSky3, //
	},
	message: {
		//
		default: ColorGrey1, //
		disabled: SpbSky3, //
		hover: ColorGrey1, // :hover
		focus: ColorGrey1, // :focus
		error: ColorGrey1, //
	},
	border: {
		//
		default: SpbSky2, //
		disabled: ColorWhite, //
		hover: ColorGrey1, // :hover
		focus: ColorGrey1, // :focus
		error: Fury, //
	},
	notice: {
		// ( )
		default: SpbSky3, //
		disabled: SpbSky3, //
		hover: SpbSky3, // :hover
		focus: SpbSky3, // :focus
		error: Fury, //
	},
};

const search = {
	background: {
		//
		default: SpbSky0, //
		focus: SpbSky0, // :focus
	},
	text: {
		//
		default: ColorGrey1, //
		focus: ColorGrey1, // :focus
	},
	icons: {
		//
		search: {
			// ( )
			default: SpbSky3, //
			focus: ColorGrey1, // :focus
		},
		close: {
			//
			default: SpbSky3, //
			focus: SpbSky3, // :focus
		},
	},
	placeholder: {
		// (placeholder)
		default: SpbSky3, //
		focus: SpbSky3, // :focus
	},
};

const bubble = {
	operator: {
		background: SpbSky0, //
		message: ColorGrey1, //
		title: SpbSky3, //
		icon: ColorGrey1, // ()
		status: SpbSky3, //
		time: {
			default: SpbSky3, //
			error: ColorError, // /
		},
	},
	client: {
		background: LightSoftGreen, //
		message: ColorGrey1, //
		title: SpbSky3, //
		icon: ColorGrey1, // ()
		time: {
			default: BrandColorMain, //
			error: ColorError, // /
		},
	},
	attachment: {
		background: ColorWhite, //
		text: ColorGrey1, //
		icon: ColorGrey1, //
		caption: SpbSky3, //
		timeout: {
			icon: ColorWhite, //
			background: SpbSky3, //
		},
		modal: {
			desktop: {
				background: 'rgba(51, 51, 51, 0.5)', //
				text: {
					background: 'rgba(0, 0, 0, 0.8)', //
					color: ColorWhite, //
				},
				icon: ColorGrey1, //
			},
			mobile: {
				icon: BrandColorMain, //
			},
		},
	},
};

const modal = {
	background: ColorWhite, //
	border: 'rgba(255, 255, 255, 0)', //
	shadow: 'rgba(0, 0, 0, 0.1)', //
	text: {
		main: ColorGrey1, //
		secondary: SpbSky3, //
	},
};

const scrollbar = {
	track: ColorWhite, //
	thumb: SpbSky2, //
};

const menu = {
	background: ColorWhite, //
	tab: SpbSky0, //
	text: ColorGrey1, //
	icon: ColorGrey1, //
	shadow: 'rgba(0, 0, 0, 0.08)', //
};

const header = {
	background: ColorWhite, //
	text: ColorGrey1, //
	elements: {
		background: SpbSky1, //
		hover: 'rgba(51, 51, 51, 0.1)', // hover
		active: 'rgba(51, 51, 51, 0.2)', // active
		text: SpbSky3, //
		icon: SpbSky3, //
	},
	search: {
		background: ColorWhite, //
		input: {
			background: SpbSky0, //
			text: ColorGrey1, //
			placeholder: ColorGrey3, //
		},
		icons: {
			search: {
				default: SpbSky3, // ()
				focus: ColorGrey1, // focus
			},
			cancel: {
				default: SpbSky3, //
			},
		},
		result: {
			client: Purple, //
			operator: BrandColorMain, //
		},
	},
};

const footer = {
	background: ColorWhite, //
	shadow: 'rgba(0, 0, 0, 0.16)', //
	text: ColorGrey1, //
	icon: ColorGrey1,
	elements: {
		search: {
			background: ColorWhite, //
			result: BrandColorMain, // (1 25)
			noResult: ColorGrey1, //
			searchResult: SpbSky3,
			icon: {
				enabled: ColorGrey1, //
				disabled: SpbSky3, //
			},
		},
		image: {
			text: ColorGrey1, //
		},
		preview: {
			border: SpbSky1, //
		},
		error: {
			background: DarkPeach, //
			text: ColorGrey1, //
			icon: {
				default: ColorWarning, //
				background: ColorWhite, //
			},
		},
	},
	notification: {
		background: ColorGrey1, //
		text: ColorWhite, //
		icons: {
			close: SpbSky3, //
			info: LightFury, //
		},
	},
	hints: {
		background: {
			default: SpbSky0, //
			hover: SpbSky1, //
		},
		text: ColorGrey1, //
		icon: {
			fill: ColorGrey1, //
			opacity: 0.5, //
		},
	},
};

const scales = {
	shadow: 'rgba(0,0,0,0.3)',
	text: {
		default: SpbSky0,
		hover: SpbSky0,
		active: SpbSky0,
		disabled: SpbSky3,
	},
	scale: {
		default: {
			default: ColorWhite,
			hover: ColorWhite,
			active: ColorWhite,
			disabled: ColorWhite,
		},
		select: {
			hover: BrandColorMain,
			active: BrandColorMainDarken,
			disabled: SpbSky3,
		},
	},
	knob: {
		default: ColorWhite,
		hover: ColorWhite,
		active: ColorWhite,
		disabled: ColorWhite,
	},
};

const selectColors = {
	text: {
		default: SpbSky0,
		hover: SpbSky0,
		disabled: SpbSky3,
		focus: SpbSky0,
		press: SpbSky1,
	},
	border: {
		default: SpbSky2, //
		disabled: ColorWhite, //
		hover: ColorGrey1, // :hover
		focus: ColorGrey1, // :focus
		press: SpbSky3,
	},
	select: {
		default: ColorWhite,
		disabled: ColorWhite,
		hover: ColorWhite,
		focus: ColorWhite,
		press: ColorWhite,
	},
	background: {
		default: {
			default: SpbSky0,
			disabled: ColorWhite,
			hover: SpbSky1,
			focus: SpbSky1,
			press: SpbSky2,
		},
		select: {
			default: SpbSky0,
			disabled: ColorWhite,
			hover: ColorWhite,
			focus: ColorWhite,
			press: ColorWhite,
		},
	},
};

const chip = {
	background: ColorWhite, //
	text: ColorGrey1, //
};

const icon = {
	default: ColorGrey1, // -
	disabled: SpbSky3, //
};

const calendar = {
	cell: {
		//
		default: {
			background: {
				default: 'none', // -
				hover: SpbSky0, // hover
				before: ColorWhite, //
			},
			color: SpbSky3, //
		},
		selected: {
			background: {
				default: SpbSky0, //
				hover: SpbSky1, // hover
			},
			color: ColorWhite, //
		},
	},
};

const loader = {
	color1: BrandColorMain,
	color2: BrandColorMain,
	color3: BrandPurple,
};

const colors = {
	buttons,
	input,
	search,
	bubble,
	modal,
	scrollbar,
	menu,
	header,
	footer,
	scales,
	select: selectColors,
	chip,
	icon,
	calendar,
	loader,
};

export const config = {
	userData,
	theme: {
		font: {
			fontFamily: fonts.fontFamilies.regular,
			fontWeight: {
				regular: '400',
				medium: '500',
				bold: '600',
			},
		},
		colors,
	},
	metrics,
	medias,
};
